import type { AstDocument, Block } from '@emailforge/schemas';

/**
 * Per-block change classification between two AST documents. Useful for the
 * version diff viewer — we want a flat list of "what changed" the user can
 * scan quickly, not a generic JSON diff (those are unreadable for templates).
 *
 * Block identity is the `id` field. Modifications report which top-level prop
 * keys changed; we don't recurse into nested props because email blocks are
 * shallow (text/href/color/etc are first-level).
 */

export type BlockChange =
  | { kind: 'added'; block: Block; index: number }
  | { kind: 'removed'; block: Block; index: number }
  | { kind: 'moved'; block: Block; fromIndex: number; toIndex: number }
  | { kind: 'modified'; before: Block; after: Block; changedProps: string[] };

export type AstDiff = {
  changes: BlockChange[];
  /** Quick stats for the summary banner. */
  summary: { added: number; removed: number; moved: number; modified: number };
};

export function diffAst(before: AstDocument, after: AstDocument): AstDiff {
  const beforeMap = new Map(before.blocks.map((b, i) => [b.id, { block: b, index: i }]));
  const afterMap = new Map(after.blocks.map((b, i) => [b.id, { block: b, index: i }]));
  const changes: BlockChange[] = [];

  for (const [id, { block, index }] of beforeMap) {
    if (!afterMap.has(id)) {
      changes.push({ kind: 'removed', block, index });
    }
  }
  for (const [id, { block: afterBlock, index: afterIndex }] of afterMap) {
    const prev = beforeMap.get(id);
    if (!prev) {
      changes.push({ kind: 'added', block: afterBlock, index: afterIndex });
      continue;
    }
    if (prev.index !== afterIndex) {
      changes.push({
        kind: 'moved',
        block: afterBlock,
        fromIndex: prev.index,
        toIndex: afterIndex,
      });
    }
    const changedProps = diffProps(prev.block.props as Record<string, unknown>, afterBlock.props as Record<string, unknown>);
    if (changedProps.length > 0) {
      changes.push({
        kind: 'modified',
        before: prev.block,
        after: afterBlock,
        changedProps,
      });
    }
  }

  // Sort by current (after) position so the user reads top-to-bottom.
  changes.sort((a, b) => positionOf(a, after) - positionOf(b, after));

  const summary = {
    added: 0,
    removed: 0,
    moved: 0,
    modified: 0,
  };
  for (const c of changes) summary[c.kind] += 1;

  return { changes, summary };
}

function positionOf(c: BlockChange, after: AstDocument): number {
  if (c.kind === 'removed') return 1e9; // bottom
  const id =
    c.kind === 'added' ? c.block.id : c.kind === 'moved' ? c.block.id : c.after.id;
  const idx = after.blocks.findIndex((b) => b.id === id);
  return idx === -1 ? 1e9 : idx;
}

function diffProps(before: Record<string, unknown>, after: Record<string, unknown>): string[] {
  const keys = new Set([...Object.keys(before ?? {}), ...Object.keys(after ?? {})]);
  const changed: string[] = [];
  for (const k of keys) {
    if (!shallowEqual(before?.[k], after?.[k])) changed.push(k);
  }
  return changed;
}

function shallowEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a == null || b == null) return false;
  if (typeof a !== typeof b) return false;
  if (typeof a !== 'object') return false;
  // For objects/arrays we stringify — block props are JSON-safe so this is
  // both correct and order-sensitive (which is what we want — `[1,2]` vs
  // `[2,1]` should be flagged).
  return JSON.stringify(a) === JSON.stringify(b);
}
